import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiAlertCircle, FiArrowRight, FiEye, FiEyeOff, FiLock } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';

export default function AdminAuth() {
  const [mode, setMode] = useState('login');
  const [form, setForm] = useState({ fullName: '', email: '', password: '', confirm: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [capsOn, setCapsOn] = useState(false);
  const { adminLogin, adminRegister } = useAuth();
  const navigate = useNavigate();

  const handleChange = e => { setForm(p => ({ ...p, [e.target.name]: e.target.value })); setError(''); };
  const switchMode = (next) => { setMode(next); setError(''); };
  const checkCaps = (e) => setCapsOn(Boolean(e.getModifierState && e.getModifierState('CapsLock')));

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');
    if (mode === 'register' && form.password !== form.confirm) {
      setError('Passwords do not match.'); return;
    }
    setLoading(true);
    try {
      if (mode === 'login') {
        await adminLogin(form.email, form.password);
      } else {
        await adminRegister(form.fullName, form.email, form.password);
      }
      navigate('/admin');
    } catch (err) {
      setError(err.response?.data?.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const isLogin = mode === 'login';
  const len = form.password.length;
  const strength = len === 0 ? 0 : len < 6 ? 1 : len < 10 ? 2 : 3;
  const mismatch = !isLogin && form.confirm.length > 0 && form.confirm !== form.password;
  const match = !isLogin && form.confirm.length > 0 && form.confirm === form.password;

  return (
    <div className="aa-page">
      <style>{CSS}</style>

      <aside className="aa-brand">
        <div className="aa-brand-top">
          <span className="aa-logo"><img src="/logo.png" alt="UAC Foods Logo" /></span>
        </div>
        <div className="aa-brand-copy">
          <span className="aa-eyebrow">Administrator portal</span>
          <h1>UAC Foods<br />Sales Leadership<br />Assessment</h1>
          <p>A proprietary talent evaluation framework designed to identify top-tier commercial leadership across Nigeria's most competitive FMCG markets.</p>
        </div>
        <blockquote>"Calibrated for strategic thinking, commercial acumen, and organizational leadership."</blockquote>
      </aside>

      <main className="aa-main">
        <div className="aa-card">
          <span className="aa-badge"><FiLock size={13} /> Administrators only</span>
          <h2>{isLogin ? 'Administrator sign in' : 'Create admin account'}</h2>
          <p className="aa-sub">{isLogin ? 'Access the assessment management portal.' : 'Set up your administrator access.'}</p>

          <div className="aa-seg" role="tablist" aria-label="Account">
            <button type="button" role="tab" aria-selected={isLogin} className={isLogin ? 'active' : ''} onClick={() => switchMode('login')}>Sign in</button>
            <button type="button" role="tab" aria-selected={!isLogin} className={!isLogin ? 'active' : ''} onClick={() => switchMode('register')}>Create account</button>
          </div>

          <div className="aa-error" role="alert" aria-live="assertive" hidden={!error}>
            {error && <><FiAlertCircle size={17} /><span>{error}</span></>}
          </div>

          <form onSubmit={handleSubmit} className="aa-form">
            {!isLogin && (
              <div className="aa-field">
                <label htmlFor="aa-name">Full name</label>
                <input id="aa-name" name="fullName" type="text" autoComplete="name" placeholder="Your full name" value={form.fullName} onChange={handleChange} required />
              </div>
            )}
            <div className="aa-field">
              <label htmlFor="aa-email">Email address</label>
              <input id="aa-email" name="email" type="email" autoComplete="username" inputMode="email" autoCapitalize="none" spellCheck="false" placeholder="admin@uacfoods.com" value={form.email} onChange={handleChange} required autoFocus={isLogin} />
            </div>
            <div className="aa-field">
              <label htmlFor="aa-password">Password</label>
              <div className="aa-pass">
                <input
                  id="aa-password" name="password" type={showPassword ? 'text' : 'password'}
                  autoComplete={isLogin ? 'current-password' : 'new-password'}
                  placeholder={isLogin ? 'Enter your password' : 'At least 6 characters'}
                  value={form.password} onChange={handleChange} onKeyUp={checkCaps} onKeyDown={checkCaps} onBlur={() => setCapsOn(false)}
                  required minLength={6}
                />
                <button type="button" className="aa-eye" onClick={() => setShowPassword(!showPassword)} aria-label={showPassword ? 'Hide password' : 'Show password'} aria-pressed={showPassword}>
                  {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                </button>
              </div>
              {capsOn && <p className="aa-caps"><FiAlertCircle size={13} /> Caps Lock is on</p>}
              {!isLogin && len > 0 && (
                <div className={`aa-strength s${strength}`} aria-label={`Password strength ${['', 'weak', 'fair', 'strong'][strength]}`}>
                  <i /><i /><i /><span>{['', 'Too short', 'Fair', 'Strong'][strength]}</span>
                </div>
              )}
            </div>
            {!isLogin && (
              <div className="aa-field">
                <label htmlFor="aa-confirm">Confirm password</label>
                <div className="aa-pass">
                  <input id="aa-confirm" name="confirm" type={showConfirm ? 'text' : 'password'} autoComplete="new-password" placeholder="Repeat your password" value={form.confirm} onChange={handleChange} required />
                  <button type="button" className="aa-eye" onClick={() => setShowConfirm(!showConfirm)} aria-label={showConfirm ? 'Hide password' : 'Show password'} aria-pressed={showConfirm}>
                    {showConfirm ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                  </button>
                </div>
                {(match || mismatch) && <p className={`aa-match ${match ? 'ok' : 'bad'}`}>{match ? 'Passwords match' : 'Passwords do not match yet'}</p>}
              </div>
            )}
            <button className="aa-submit" type="submit" disabled={loading}>
              {loading ? <><span className="aa-spinner" /> Please wait…</> : <>{isLogin ? 'Sign in' : 'Create account'} <FiArrowRight size={18} /></>}
            </button>
          </form>

          <p className="aa-footer">© {new Date().getFullYear()} UAC Foods Limited — Proprietary Assessment Tool</p>
        </div>
      </main>
    </div>
  );
}

/* Scoped styles. This page no longer imports AdminAuth.module.css */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
.aa-page { --navy:#0a2540; --navy-2:#12365c; --red:#c8102e; --ink:#0f172a; --ink-2:#475569; --ink-3:#94a3b8; --line:#d9e2ec;
  min-height:100vh; min-height:100dvh; display:flex; flex-direction:column; background:#f3f6fa; color:var(--ink); font-family:'Plus Jakarta Sans',ui-sans-serif,system-ui,sans-serif; -webkit-font-smoothing:antialiased; }
.aa-page *, .aa-page *::before, .aa-page *::after { box-sizing:border-box; }
.aa-page button, .aa-page input { font-family:inherit; }
.aa-page :focus-visible { outline:3px solid rgba(200,16,46,.28); outline-offset:2px; border-radius:10px; }
@keyframes aa-spin { to { transform:rotate(360deg); } }
@keyframes aa-rise { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:none; } }
@keyframes aa-shake { 10%,90% { transform:translateX(-1px); } 20%,80% { transform:translateX(2px); } 30%,50%,70% { transform:translateX(-4px); } 40%,60% { transform:translateX(4px); } }

.aa-brand { position:relative; display:flex; flex-direction:column; gap:12px; padding:18px 18px 40px; color:#fff; overflow:hidden;
  background:radial-gradient(110% 90% at 100% 0%, rgba(200,16,46,.38), transparent 55%), radial-gradient(90% 80% at 0% 100%, rgba(59,130,246,.22), transparent 55%), linear-gradient(160deg, var(--navy) 0%, var(--navy-2) 100%); }
.aa-brand::after { content:''; position:absolute; inset:0; background-image:linear-gradient(rgba(255,255,255,.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.05) 1px, transparent 1px); background-size:44px 44px; mask-image:linear-gradient(to bottom, rgba(0,0,0,.7), transparent); pointer-events:none; }
.aa-brand > * { position:relative; z-index:1; }
.aa-logo { display:inline-flex; padding:0; border-radius:0; background:transparent; }
.aa-logo img { display:block; height:34px; width:auto; object-fit:contain; }
.aa-eyebrow { display:inline-block; margin-bottom:7px; font-size:10px; font-weight:800; letter-spacing:.12em; text-transform:uppercase; color:rgba(255,255,255,.7); }
.aa-brand-copy h1 { margin:0; font-size:20px; line-height:1.15; font-weight:800; letter-spacing:-.03em; }
.aa-brand-copy p, .aa-brand blockquote { display:none; }

.aa-main { flex:1; display:flex; align-items:flex-start; justify-content:center; padding:0 14px 28px; }
.aa-card { position:relative; z-index:2; width:100%; max-width:440px; margin-top:-24px; padding:20px 18px 18px; border:1px solid rgba(15,23,42,.07); border-radius:20px; background:#fff; box-shadow:0 18px 42px rgba(15,23,42,.13); animation:aa-rise .4s ease both; }
.aa-badge { display:inline-flex; align-items:center; gap:6px; padding:4px 9px; border-radius:99px; background:#fdf0f2; color:var(--red); font-size:10px; font-weight:700; }
.aa-card h2 { margin:10px 0 0; font-size:21px; font-weight:800; letter-spacing:-.03em; line-height:1.15; }
.aa-sub { margin:7px 0 14px; font-size:12px; line-height:1.55; color:var(--ink-2); }
.aa-seg { display:grid; grid-template-columns:1fr 1fr; gap:4px; padding:3px; margin-bottom:12px; border-radius:12px; background:#eef2f6; }
.aa-seg button { min-height:38px; border:0; border-radius:9px; background:transparent; color:var(--ink-2); font-size:12px; font-weight:700; cursor:pointer; transition:background .15s, color .15s, box-shadow .15s; }
.aa-seg button.active { background:#fff; color:var(--navy); box-shadow:0 2px 8px rgba(15,23,42,.1); }

.aa-error { display:flex; align-items:flex-start; gap:8px; margin-bottom:12px; padding:10px 12px; border:1px solid #fecaca; border-radius:12px; background:#fef2f2; color:#991b1b; font-size:11.5px; font-weight:600; line-height:1.45; animation:aa-shake .45s ease both; }
.aa-error[hidden] { display:none; }
.aa-error svg { flex:0 0 auto; margin-top:1px; }

.aa-form { display:grid; gap:13px; }
.aa-field label { display:block; margin-bottom:6px; font-size:11px; font-weight:700; color:#334155; }
.aa-field input { width:100%; min-height:44px; padding:0 12px; border:1.5px solid var(--line); border-radius:11px; background:#fff; color:var(--ink); font-size:13px; outline:none; transition:border-color .15s, box-shadow .15s; }
.aa-field input::placeholder { color:#a8b3c2; }
.aa-field input:focus { border-color:var(--navy); box-shadow:0 0 0 4px rgba(10,37,64,.1); }
.aa-pass { position:relative; }
.aa-pass input { padding-right:46px; }
.aa-eye { position:absolute; right:3px; top:50%; display:grid; place-items:center; width:38px; height:38px; transform:translateY(-50%); border:0; border-radius:10px; background:transparent; color:#64748b; cursor:pointer; }
.aa-eye:hover { color:var(--ink); background:#f1f5f9; }
.aa-caps { display:flex; align-items:center; gap:5px; margin:7px 0 0; font-size:11px; font-weight:600; color:#b45309; }
.aa-strength { display:flex; align-items:center; gap:5px; margin-top:9px; }
.aa-strength i { flex:1; height:5px; border-radius:99px; background:#e2e8f0; }
.aa-strength span { margin-left:5px; font-size:10px; font-weight:700; color:var(--ink-2); white-space:nowrap; }
.aa-strength.s1 i:nth-child(1) { background:#ef4444; } .aa-strength.s2 i:nth-child(-n+2) { background:#f59e0b; } .aa-strength.s3 i { background:#16a34a; }
.aa-match { margin:7px 0 0; font-size:11px; font-weight:600; }
.aa-match.ok { color:#15803d; } .aa-match.bad { color:#b91c1c; }

.aa-submit { display:flex; align-items:center; justify-content:center; gap:8px; width:100%; min-height:44px; border:0; border-radius:11px; background:var(--navy); color:#fff; font-size:13px; font-weight:800; cursor:pointer; box-shadow:0 8px 18px rgba(10,37,64,.22); transition:background .2s, transform .15s; }
.aa-submit:hover:not(:disabled) { background:var(--red); transform:translateY(-1px); }
.aa-submit:disabled { opacity:.7; cursor:not-allowed; }
.aa-spinner { width:15px; height:15px; border:2px solid rgba(255,255,255,.4); border-top-color:#fff; border-radius:50%; animation:aa-spin .7s linear infinite; }
.aa-footer { margin:16px 0 0; padding-top:13px; border-top:1px solid rgba(15,23,42,.08); text-align:center; font-size:10px; color:var(--ink-3); }

@media (min-width:640px) { .aa-card { padding:26px 26px 22px; } }
@media (min-width:900px) {
  .aa-page { flex-direction:row; }
  .aa-brand { flex:1 1 54%; justify-content:space-between; gap:0; padding:38px 48px; }
  .aa-logo img { height:38px; }
  .aa-brand-copy h1 { font-size:clamp(27px, 2.7vw, 40px); }
  .aa-brand-copy p { display:block; margin:12px 0 0; max-width:480px; font-size:12.5px; line-height:1.65; color:rgba(255,255,255,.78); }
  .aa-brand blockquote { display:block; margin:0; padding-left:14px; border-left:3px solid var(--red); font-size:11.5px; font-style:italic; line-height:1.6; color:rgba(255,255,255,.75); }
  .aa-main { flex:1 1 46%; align-items:center; padding:32px; background:#fff; }
  .aa-card { margin-top:0; padding:8px; border:0; border-radius:0; box-shadow:none; }
  .aa-card h2 { font-size:25px; }
}
@media (prefers-reduced-motion:reduce) { .aa-page *, .aa-card { animation:none !important; transition:none !important; } }
`;